<template>
  <div class="trigger-card" :class="cardClass">
    <!-- Milestone Badge (only shown when isProgram) -->
    <div v-if="isProgramWorkflow" class="milestone-badge">
      <div class="milestone-icon">
        <img src="@/assets/MyWorkBench/Workflow/program_flag.svg" alt="program flag">
      </div>
      <p class="milestone-text">Start Program</p>
    </div>
    <div class="trigger-card-body">
      <!-- Header Section -->
      <div class="trigger-card-header" :class="headerClass">
        <div class="trigger-icon" :class="iconClass">
          <div class="trigger-icon-svg">
            <slot name="icon"></slot>
          </div>
        </div>

        <div class="trigger-header-info">
          <div class="trigger-title">{{ title }}</div>
          <div class="trigger-subtitle">{{ subtitle }}</div>

          <div v-if="!isConfigured" class="trigger-status-row">
            <div class="trigger-status-icon">
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.70235 16.4156C1.31639 17.0823 1.79745 17.9167 2.56778 17.9167H17.4322C18.2025 17.9167 18.6836 17.0823 18.2976 16.4156L10.8654 3.57816C10.4803 2.91288 9.51974 2.91288 9.13457 3.57816L1.70235 16.4156ZM10.8333 14.5833C10.8333 15.0436 10.4602 15.4167 10 15.4167C9.53976 15.4167 9.16667 15.0436 9.16667 14.5833C9.16667 14.1231 9.53976 13.75 10 13.75C10.4602 13.75 10.8333 14.1231 10.8333 14.5833ZM10.8333 11.25C10.8333 11.7102 10.4602 12.0833 10 12.0833C9.53976 12.0833 9.16667 11.7102 9.16667 11.25V9.58333C9.16667 9.1231 9.53976 8.75 10 8.75C10.4602 8.75 10.8333 9.1231 10.8333 9.58333V11.25Z" fill="#D10000"/>
              </svg>
            </div>
            <div class="trigger-status-text trigger-status-text--error">Needs Configuration.</div>
          </div>

          <div v-else class="trigger-status-row">
            <div class="trigger-status-icon">
              <svg viewBox="0 0 15 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.65833 8.81667L1.18333 5.34167L0 6.51667L4.65833 11.175L14.6583 1.175L13.4833 0L4.65833 8.81667Z" fill="#0A6629"/>
              </svg>
            </div>
            <div class="trigger-status-text trigger-status-text--success">Configured</div>
          </div>
        </div>
      </div>

      <!-- Content Slot - Different for each trigger type -->
      <slot name="content"></slot>

      <!-- Buttons Section -->
      <div class="trigger-buttons">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="small"
          display_name="Delete"
          :disabled="isNodeButtonDisabled()"
          customized_class="btn-delete"
          @click-button="onDeleteClick"
        >
          <template #prefix>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0V1H16V3H15V16C15 17.1 14.1 18 13 18H3C1.9 18 1 17.1 1 16V3H0V1H5V0H11ZM3 16H13V3H3V16ZM5 5H7V14H5V5ZM11 5H9V14H11V5Z" fill="currentColor"/>
            </svg>
          </template>
        </ResponsiveButton>
      </div>
    </div>

    <!-- VueFlow Handle -->
    <Handle 
      type="source" 
      :position="Position.Bottom" 
      class="trigger-handle"
      :class="{ 'trigger-handle--connected': hasOutgoingConnection }"
    >
      <div v-if="!hasOutgoingConnection" class="connector-inner">
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 11.25H11.25V20H8.75V11.25H0V8.75H8.75V0H11.25V8.75H20V11.25Z" fill="#FFFFFF"/>
        </svg>
      </div>
    </Handle>
  </div>
</template>

<script>
import { Handle, Position, useVueFlow } from '@vue-flow/core';

export default {
  name: 'LayoutTriggerCard',
  components: {
    Handle
  },
  inject: ['isNodeButtonDisabled', 'isProgram'],
  props: {
    id: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    isConfigured: {
      type: Boolean,
      default: false
    },
    isConfigureActive: {
      type: Boolean,
      default: true
    },
    showStartButton: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['delete', 'configure', 'start-workflow'],
  setup() {
    const { edges } = useVueFlow();
    return { edges };
  },
  data() {
    return {
      Position
    };
  },
  computed: {
    hasOutgoingConnection() {
      return this.edges.some(edge => edge.source === this.id);
    },
    isProgramWorkflow() {
      return this.isProgram ? this.isProgram() : false;
    },
    cardClass() {
      const classes = [this.isConfigured ? 'trigger-card--configured' : 'trigger-card--not-configured'];
      if (this.selected) {
        classes.push('trigger-card--selected');
      }
      return classes.join(' ');
    },
    headerClass() {
      return this.isConfigured ? 'trigger-card-header--configured' : 'trigger-card-header--not-configured';
    },
    iconClass() {
      return this.isConfigured ? 'trigger-icon--configured' : 'trigger-icon--not-configured';
    },
    configureClass() {
      return this.selected ? 'btn-configure--active' : 'btn-configure--disabled';
    },
    deleteButtonClass() {
      if (this.isConfigured && this.showStartButton) {
        return 'btn-delete--with-start';
      }
      return '';
    }
  },
  methods: {
    onDeleteClick() {
      this.$emit('delete');
    },
    onConfigureClick() {
      this.$emit('configure');
    },
    onStartWorkflowClick() {
      this.$emit('start-workflow');
    }
  }
};
</script>

<style scoped>
.trigger-card {
  width: 324px;
  position: relative;
}

/* Milestone Badge */
.milestone-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  background: #E1D3F1;
  border: 1px solid #C4C4C4;
  border-radius: 24px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0px 2px 5px 0px rgba(74, 74, 74, 0.25);
}

.milestone-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.milestone-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333333;
  margin: 0;
  white-space: nowrap;
}

.trigger-card-body {
  padding: 24px 18px;
  border-radius: 80px 80px 10px 10px;
  box-shadow: 0 1px 15px rgba(164, 164, 164, 0.25);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.trigger-card--not-configured .trigger-card-body {
  background: #FEF9E9;
  border: 1px solid #D10000;
  transition: border-color 0.2s ease;
}

.trigger-card--configured .trigger-card-body {
  background: #F1F9F5;
  border: 1px solid #20A7C1;
  transition: border-color 0.2s ease;
}

/* Hover Effect */
.trigger-card:hover .trigger-card-body {
  border-color: #004879;
  border-width: 1.5px;
}

/* Selected Effect */
.trigger-card--selected .trigger-card-body {
  border-color: #004879;
  border-width: 1.5px;
}

.trigger-card-header {
  padding: 12px 8px;
  border-radius: 20px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.trigger-card-header--not-configured {
  background: #FDF0E3;
}

.trigger-card-header--configured {
  background: #D5F5E0;
}

.trigger-icon {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.trigger-icon--not-configured {
  background: #FEB52E;
  transition: background-color 0.2s ease;
}

.trigger-icon--configured {
  background: #25B8C2;
  transition: background-color 0.2s ease;
}

/* Hover Effect - Icon Background */
.trigger-card:hover .trigger-icon {
  background: #004879;
}

/* Selected Effect - Icon Background */
.trigger-card--selected .trigger-icon {
  background: #004879;
}

.trigger-icon-svg {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trigger-icon-svg svg {
  width: 100%;
  height: 100%;
}

.trigger-header-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 2px;
}

.trigger-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #374957;
}

.trigger-subtitle {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  margin-bottom: 4px;
  color: #333;
}

.trigger-status-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.trigger-status-icon {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.trigger-status-icon svg {
  width: 100%;
  height: 100%;
}

.trigger-status-text {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.trigger-status-text--error {
  color: #D10000;
}

.trigger-status-text--success {
  color: #0A6629;
}

.trigger-buttons {
  padding-top: 8px;
  display: flex;
  justify-content: center;
}

.btn-delete {
  width: 136px;
}

.btn-delete :deep(.responsive-button) {
  width: 100%;
}

.trigger-handle {
  width: 28px !important;
  height: 28px !important;
  border-radius: 15px;
  border: none;
  bottom: -14px !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trigger-card--not-configured .trigger-handle:not(.trigger-handle--connected) {
  background: #FEB52E !important;
  border: 5px solid #FEB52E;
  transition: all 0.2s ease;
}

.trigger-card--configured .trigger-handle:not(.trigger-handle--connected) {
  background: #25B8C2 !important;
  border: 5px solid #25B8C2;
  transition: all 0.2s ease;
}

/* Connected state - filled with connection color */
.trigger-handle--connected {
  background: #6f4999 !important;
  border: none !important;
}

/* Hover Effect - Handler */
.trigger-card:hover .trigger-handle:not(.trigger-handle--connected) {
  border-color: #004879 !important;
  background: #004879 !important;
}

.trigger-card:hover .trigger-handle--connected:not(.trigger-handle--connected) {
  background: #004879 !important;
}

.connector-inner {
  width: 36px;
  height: 36px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
